import { Box, Button, Center, Heading, HStack, Icon, Image, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { FaCommentDots, FaGift } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeProvider';
import { useProfile } from '@/contexts/ProfileProvider';
import { useTiledeskConfig } from '@/service/tiledesk/hooks';
import { openTiledeskWidget, useTiledeskWidget } from '@/service/tiledesk/useTiledeskWidget';
import {
  FaRegUser,
  FaRegClipboard,
  FaRegEnvelope,
  FaRegUserCircle,
  FaRegHandshake,
  FaRegFileAlt,
  FaShieldAlt,
  FaShoppingBag,
  FaChartBar,
} from 'react-icons/fa';

/**
 * Verde do WhatsApp. O chat deve ler como "app de mensagem" sem usar a marca do
 * WhatsApp — por isso a cor, mas nunca o glifo. A cor do widget em si é configurada
 * no projeto do convênio no Tiledesk (Settings → Widget).
 */
const CHAT_GREEN = '#25D366';

const ProfileLink = ({
  name,
  color,
  to,
  icon,
  highlight,
  onClick,
}: {
  color: string;
  name: string;
  to?: string;
  icon: any;
  highlight?: boolean;
  onClick?: () => void;
}) => {
  const navigate = useNavigate();
  return (
    <Box
      as="button"
      onClick={onClick ?? (() => navigate(to as string))}
      borderRadius="lg"
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'space-between'}
      alignItems={'flex-start'}
      backgroundColor="white"
      overflow="hidden"
      height="110px"
      padding="20px"
      boxShadow={'2px 2px 6px 0px #0000002E'}
      width="100%"
      position="relative"
    >
      {highlight && (
        <Box w="100%" h="6px" bg={color} borderTopLeftRadius="lg" borderTopRightRadius="lg" position="absolute" top={0} left={0} />
      )}
      <Icon color={color} as={icon} boxSize={6} minW={6} minH={6} maxW={6} maxH={6} marginBottom="10px" zIndex={1} />
      <Heading color="#292927" fontWeight={'500'} textAlign={'left'} fontSize="16px" zIndex={1}>
        {name}
      </Heading>
    </Box>
  );
};

export const ReferralCard = ({ color }: { color: string }) => {
  const navigate = useNavigate();
  const { referralImage, referralOffer, referralValue } = useTheme();

  return (
    <Box
      as="button"
      onClick={() => navigate('indicacoes')}
      borderRadius="24px"
      display="flex"
      flexDirection="column"
      minWidth={300}
      backgroundColor="white"
      overflow="hidden"
      boxShadow="4px 4px 10px 0px #0000002E"
      width="100%"
      maxWidth={400}
      maxHeight={300}
      height="100%"
      textAlign="left"
    >
      {referralImage ? (
        <Image src={referralImage} alt="Indique e Ganhe" width="100%" height="55%" objectFit="cover" flexShrink={0} />
      ) : (
        <Center width="100%" height="55%" flexShrink={0} bgGradient={`linear(135deg, ${color}CC, ${color}66)`}>
          <Icon as={FaGift} boxSize={10} color="white" />
        </Center>
      )}
      <VStack align="flex-start" spacing={1} padding="14px" flex="1" justifyContent="space-between">
        <Heading
          color="#292927"
          fontWeight={600}
          fontSize="15px"
          dangerouslySetInnerHTML={{ __html: referralOffer || 'Indique e Ganhe' }}
        />
        <HStack>
          <Text fontSize="13px" color="#747474">
            Comissão:
          </Text>
          <Text fontSize="14px" fontWeight={700} color={color}>
            R${' '}
            {Number(referralValue ?? 0)
              .toFixed(2)
              .replace('.', ',')}
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
};

function SectionTitle({ title, color }: { title: string; color: string }) {
  return (
    <Heading
      fontSize="18px"
      fontWeight="600"
      color={color}
      width="100%"
      paddingBottom="4px"
      borderBottom={`2px solid ${color}`}
      marginTop="16px"
    >
      {title}
    </Heading>
  );
}

export function ProfileLinks() {
  const { profile, logout, subscriptions: rawSubscriptions } = useProfile();
  const { product, colors, getValidSubscriptions, bePartnerModule, localModule, ecommerceModule } = useTheme();
  const subscriptions = getValidSubscriptions(rawSubscriptions);
  const { data: chatConfig } = useTiledeskConfig(!!profile);

  useTiledeskWidget(
    chatConfig?.enabled && chatConfig.projectId && chatConfig.widgetUrl && chatConfig.token
      ? {
          projectId: chatConfig.projectId,
          widgetUrl: chatConfig.widgetUrl,
          token: chatConfig.token,
        }
      : undefined
  );

  // Dependentes só fazem sentido com clube (local/online) e assinatura do produto atual
  const canManageDependents =
    (localModule || ecommerceModule) && subscriptions.some((sub: any) => sub.plan.product === product?.id);

  const isPlanProductEnable = product?.metadata?.plansalemodulo?.value !== 'true';

  if (!profile) return <></>;

  return (
    <VStack w={'100%'} spacing={4}>
      {/* Seção Conta */}
      <SectionTitle title="Conta" color={colors.primaryColor} />
      <SimpleGrid columns={[2, 4]} spacing={3} width="100%">
        <ProfileLink color={colors.primaryColor} name="Meus Dados" to="meus-dados" icon={FaRegUser} />
        {isPlanProductEnable && (
          <ProfileLink name="Meus Produtos" color={colors.primaryColor} to={`planos`} icon={FaRegClipboard}  />
        )}
        <ProfileLink color={colors.primaryColor} name="Fale Conosco" to="/contato" icon={FaRegEnvelope} />
        {chatConfig?.enabled && (
          <ProfileLink
            color={CHAT_GREEN}
            name="Chat de Atendimento"
            icon={FaCommentDots}
            highlight
            onClick={openTiledeskWidget}
          />
        )}
        {canManageDependents && (
          <ProfileLink
            color={colors.primaryColor}
            name="Gerenciar Dependentes"
            to="dependentes"
            icon={FaRegUserCircle}
          />
        )}
        {bePartnerModule && (
          <ProfileLink
            color={colors.primaryColor}
            name="Seja Parceiro"
            to="/cadastro-parcerias"
            icon={FaRegHandshake}
          />
        )}
        {product?.metadata?.econometermodule?.value === 'true' && (
          <ProfileLink color={colors.primaryColor} name="Econômetro" to="/perfil/econometro" icon={FaChartBar} />
        )}
        <ProfileLink color={colors.primaryColor} name="Minhas Compras" to="/historico" icon={FaShoppingBag} />
        <ProfileLink color={colors.primaryColor} name="Termos de Uso" to="/legal/termos-de-uso" icon={FaRegFileAlt} />
        <ProfileLink
          color={colors.primaryColor}
          name="Política De Privacidade"
          to="/legal/politica-de-privacidade"
          icon={FaShieldAlt}
        />
      </SimpleGrid>

      <Button variant={''} marginTop={25} onClick={logout}>
        <Heading color="#747474" fontWeight={'600'} fontFamily={'Glory'} fontSize={'18px'}>
          SAIR
        </Heading>
      </Button>
    </VStack>
  );
}
